import { Box, IconButton, InputAdornment, Tooltip } from "@mui/material"
import { icons } from "../../../../utils/icons"
import React, { useEffect, useRef, useState } from "react"
import { InputModel } from "../../../../utils/types"
import { useTranslation } from "react-i18next"
import { useStyles } from "./styles"
import { useFilter } from "../../../../hooks/filter"
import { ViewMode } from "./enums/enums"
import { ListFormRef } from "./components/form/types"
import { ListDropDown } from "../../../../service/dropDownLists/response"
import { returnQuery } from "../../../../utils/methods/returnQuery"
import { dropDownListServiceFetch } from "../../../../utils/constants/callService"
import { formatDropDownList, formatItemList } from "../../../../service/dropDownLists/format"
import { RequestErrors, Types } from "../../../../utils/enums"
import { handleAllErrors } from "../../../../utils/methods/handleErrors"


const useBody = () => {

  const { t } = useTranslation()
  const styles = useStyles()
  const filterDropList = useFilter({ formatFilter: {} })
  const [totalPages, setTotalPages] = useState(0)
  const [isLoading, setIsLoading] = useState(false)
  const [mode, setMode] = useState<ViewMode>(ViewMode.CREATE)
  const [nameEdit, setNameEdit] = useState<string | undefined>(undefined)
  const [valuesEdit, setValuesEdit] = useState<{ id: number, value: string }[]>([])
  const [idEdit, setIdEdit] = useState<number | undefined>(undefined)
  const refTime = useRef<NodeJS.Timeout | null>(null)
  const formRef = useRef<ListFormRef>(null);
  const [isLoadingModal, setIsLoadingModal] = useState(false)
  const [touched, setTouched] = useState(false)
  const [loadingSubmit, setLoadingSubmit] = useState(false)
  const [loadingItem, setLoadingItem] = useState(false)

  const [row, setRow] = useState<ListDropDown[]>([])

  const [modal, setModal] = useState<{
    open: boolean
    title: string
    description?: string
    children?: JSX.Element
    removeCancel?: boolean
    confirm: () => void
  }>({
    open: false,
    title: '',
    description: '',
    confirm: () => { },
  })

  const [snackbarOpen, setSnackbarOpen] = useState({
    open: false,
    message: '',
    type: Types.SUCCESS,
  })

  const input: { [x: string]: InputModel } = {
    search: {
      label: 'dropList.tags.searchList',
      translate: true,
      name: 'search',
      rest: {
        type: 'alphaNumeric',
        placeholder: "Buscar listas"
      },
      value: null,
      wrapChildren: ({ children, id }) => {
        return React.cloneElement(children, {
          id,
          InputProps: {
            ...(children.props.InputProps ?? {}),
            endAdornment: (
              <InputAdornment position="end">
                {icons('search')}
              </InputAdornment>
            ),
          },
        });
      }
    },
  }

  const column: any[] = [
    {
      id: 'name',
      label: t('dropList.tags.listName'),
      sortable: true,
    },
    {
      id: 'valuesCount',
      label: t('dropList.tags.values'),
      sortable: true,
    },
    {
      id: 'relationsCount',
      label: t('dropList.tags.relations'),
      sortable: true,
    },
    {
      id: 'action',
      label: '',
      renderCell: (params: any) => {
        const row = params.row
        return (
          <Box sx={styles.constainerAction}>
            <Tooltip title={t('ToolTips.edit')}>
              <IconButton
                sx={styles.edit}
                onClick={(e) => {
                  e.stopPropagation()
                  handleEdit(row.id)
                }}
              >
                {icons('edit')}
              </IconButton>
            </Tooltip>
            <Tooltip title={t('ToolTips.delete')}>
              <IconButton
                sx={styles.delete}
                onClick={(e) => {
                  e.stopPropagation()
                  handleDelete(row.id)
                }}
              >
                {icons('delete')}
              </IconButton>
            </Tooltip>
          </Box>
        )
      }
    },
  ]

  const closeModal = () => {
    setModal(prev => ({
      ...prev,
      open: false,
    }))
  }

  const closeSnackBarOpen = () => {
    setSnackbarOpen(prev => ({ ...prev, open: false }))
  }

  const handleCreate = () => {
    if (mode !== ViewMode.CREATE) {
      setModal(prev => ({
        ...prev,
        open: true,
        title: t('dropList.modal.titles.confirmation'),
        description: t('dropList.modal.descriptions.cancelEdition'),
        confirm: () => {
          setNameEdit('')
          setValuesEdit([])
          setIdEdit(undefined)
          setMode(ViewMode.CREATE)
          setTouched(false)
          closeModal()
        }
      }))
    }
  }

  const getItemList = async (id: number) => {
    setLoadingItem(true)

    const response = await dropDownListServiceFetch.getListById({ id });

    if (!response.errors) {
      const { items, name } = formatItemList({ response })
      setMode(ViewMode.EDIT)
      setIdEdit(id)
      setTouched(true)
      setNameEdit(name)
      setValuesEdit(items)
    } else {
      setSnackbarOpen(prev => ({ ...prev, open: true, message: handleAllErrors(response?.errors?.[0]?.code ?? '' as RequestErrors, t), type: Types.ERROR }))
    }

    setLoadingItem(false)
  }

  const handleEdit = (id: number) => {
    if (mode == ViewMode.CREATE && formRef.current?.hasData()) {
      setModal(prev => ({
        ...prev,
        open: true,
        title: t('dropList.modal.titles.confirmation'),
        description: t('dropList.modal.descriptions.cancelCreate'),
        confirm: () => {
          getItemList(id)
          closeModal()
        }
      }))
    } else {
      if(!idEdit){
        getItemList(id)
      }
      if (idEdit && idEdit !== id) {
        setModal(prev => ({
          ...prev,
          open: true,
          title: t('dropList.modal.titles.confirmation'),
          description: t('dropList.modal.descriptions.cancelEdition'),
          confirm: () => {
            getItemList(id)
            closeModal()
          }
        }))
      } 
    }
  }

  const handleDelete = (id: number) => {
    setModal(prev => ({
      ...prev,
      open: true,
      title: t('dropList.modal.titles.confirmation'),
      description: t('dropList.modal.descriptions.confirmDelete'),
      confirm: () => {
        deleteList(id)
      }
    }))
  }

  const hadleSubmit = async (name: string, editValues: { id: number; value: string; }[]) => {
    setLoadingSubmit(true)

    if (!idEdit) {
      //creación
      const body = {
        name: name,
        options: editValues.map(ev => (ev.value))
      }
      const response = await dropDownListServiceFetch.createList(body);

      if (!response.errors) {
        setSnackbarOpen(prev => ({ ...prev, open: true, message: t('dropList.snackBar.success.createList'), type: Types.SUCCESS }))
        setNameEdit('')
        setValuesEdit([])
        setMode(ViewMode.CREATE)
        setTouched(false)
        setIdEdit(undefined)
        getDropList()
      } else {
        setSnackbarOpen(prev => ({ ...prev, open: true, message: response?.errors?.[0]?.code == RequestErrors.VALIDATION_ERROR ? t('general.messages.errors.DUPLICATE_OPTIONS') : handleAllErrors(response?.errors?.[0]?.code ?? '' as RequestErrors, t), type: Types.ERROR }))
      }
    } else {
      //edición
      const oldValues = valuesEdit.map(v => v.value);
      const newValues = editValues.map(v => v.value);
      const addOptions = newValues.filter(v => !oldValues.includes(v));
      const removeOptionIds = valuesEdit
        .filter(v => !newValues.includes(v.value))
        .map(v => v.id);

      const body: any = {
        name,
        ...(addOptions.length && { addOptions }),
        ...(removeOptionIds.length && { removeOptionIds }),
      };
      const response = await dropDownListServiceFetch.editList({ body, id: idEdit });

      if (!response.errors) {
        setSnackbarOpen(prev => ({ ...prev, open: true, message: t('dropList.snackBar.success.editList'), type: Types.SUCCESS }))
        setNameEdit('')
        setValuesEdit([])
        setMode(ViewMode.CREATE)
        setTouched(false)
        setIdEdit(undefined)
        getDropList()
      } else {
        setSnackbarOpen(prev => ({ ...prev, open: true, message: response?.errors?.[0]?.code == RequestErrors.VALIDATION_ERROR ? t('general.messages.errors.DUPLICATE_OPTIONS') : handleAllErrors(response?.errors?.[0]?.code ?? '' as RequestErrors, t), type: Types.ERROR }))
      }
    }

    setLoadingSubmit(false)
  }

  const deleteList = async (id: number) => {
    setIsLoadingModal(true)

    const response = await dropDownListServiceFetch.deleteList(id);

    if (!response.errors) {
      setSnackbarOpen(prev => ({ ...prev, open: true, message: t('dropList.snackBar.success.deleteList'), type: Types.SUCCESS }))
      if (id == idEdit) {
        setIdEdit(undefined)
        setNameEdit('')
        setValuesEdit([])
        setMode(ViewMode.CREATE)
        setTouched(false)
      }
      getDropList()
    } else {
      setSnackbarOpen(prev => ({ ...prev, open: true, message: handleAllErrors(response?.errors?.[0]?.code ?? '' as RequestErrors, t), type: Types.ERROR }))
    }

    setIsLoadingModal(false)
    closeModal()
  }


  const getDropList = async () => {
    setIsLoading(true)
    const response = await dropDownListServiceFetch.getDropList({ query: returnQuery({ ...filterDropList.query, filter: filterDropList.query.filter?.replaceAll('+', '%2B') }, {}) })
    const { row } = formatDropDownList({ response })

    if (!response?.errors) {
      setRow(row)
      setTotalPages(response.totalPages)
      if(!response.items.length && response.currentPage>response.totalPages){
        filterDropList.handlePagination({ page: response.totalPages, pageSize: 20 })
      }
    }

    setIsLoading(false)
  }

  useEffect(() => {
    if (refTime.current) { clearTimeout(refTime.current) }

    refTime.current = setTimeout(() => {
      getDropList()
    }, 300)
  }, [filterDropList.query.all])


  return { setIdEdit, loadingItem, hadleSubmit, loadingSubmit, touched, setTouched, snackbarOpen, closeSnackBarOpen, isLoadingModal, mode, modal, formRef, setModal, handleCreate, closeModal, setMode, input, filterDropList, totalPages, isLoading, column, row, nameEdit, valuesEdit, idEdit }
}

export { useBody }


//Limpiar global name y values al cargar la lista de nuevo